import React, { useCallback, useEffect, useState } from "react";
import { Tab, Tabs, Form } from "react-bootstrap";
import { getMyMlsListings } from "@/services/v2/mlsListings";
import { SelectDropDown } from "@/components";
import MyMlsListingsRow from "./MyMlsListingsRow";

const STATUS_OPTIONS = [
  { key: "all", label: "All", apiValue: null },
  { key: "active", label: "Active", apiValue: "active" },
  { key: "pending", label: "Pending", apiValue: "pending" },
  { key: "sold", label: "Sold", apiValue: "sold" },
];

const SORT_OPTIONS = [
  { value: "NEWEST", label: "Newest" },
  { value: "OLDEST", label: "Oldest" },
];

const PAGE_LIMIT = 10;

const PreSsoEmptyState = () => (
  <div className="no-result-wrap mls-empty-state">
    <h3>Your MLS listings haven't loaded yet</h3>
    <p>
      We haven't matched your MLS profile to Gitcha yet. Once your MLS sign-in
      is linked, your listings will appear here automatically.
    </p>
    <p className="mls-empty-state__support">
      Expecting to see listings here?{" "}
      <a
        href="mailto:support@gitcha.com"
        rel="noopener noreferrer"
      >
        Contact support
      </a>
    </p>
  </div>
);

const FilterEmptyState = ({ statusLabel }) => (
  <div className="no-result-wrap mls-empty-state">
    <h3>No listings match this filter</h3>
    <p>
      No {statusLabel.toLowerCase()} MLS listings found. Try a different
      status.
    </p>
  </div>
);

const ErrorState = ({ onRetry }) => (
  <div className="no-result-wrap mls-empty-state">
    <h3>Couldn't load your MLS listings</h3>
    <p>Something went wrong. Please try again.</p>
    <button className="btn btn-primary" onClick={onRetry}>
      Retry
    </button>
  </div>
);

const MyMlsListingsTab = () => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [statusFilter, setStatusFilter] = useState(STATUS_OPTIONS[0]);
  const [sortType, setSortType] = useState(SORT_OPTIONS[0].value);

  const fetchListings = useCallback(
    async (opts = {}) => {
      const requestedPage = opts.page ?? 1;
      const requestedFilter = opts.statusFilter ?? statusFilter;
      const requestedSort = opts.sortType ?? sortType;

      setLoading(true);
      setHasError(false);

      try {
        const query = {
          page: requestedPage,
          limit: PAGE_LIMIT,
          sortType: requestedSort,
        };
        if (requestedFilter?.apiValue) {
          query.status = requestedFilter.apiValue;
        }
        const response = await getMyMlsListings(query);
        const payload = response?.data || {};
        const list = payload.data || [];
        setTotalCount(payload.totalCount || 0);
        setHasNextPage(!!payload.hasNextPage);
        setRows((prev) =>
          requestedPage > 1 ? [...prev, ...list] : list,
        );
      } catch (err) {
        setHasError(true);
        if (requestedPage === 1) {
          setRows([]);
          setTotalCount(0);
          setHasNextPage(false);
        }
      } finally {
        setLoading(false);
      }
    },
    [statusFilter, sortType],
  );

  useEffect(() => {
    setPage(1);
    fetchListings({ page: 1, statusFilter, sortType });
  }, [statusFilter, sortType, fetchListings]);

  const handleStatusChange = (key) => {
    const next = STATUS_OPTIONS.find((s) => s.key === key);
    if (next) setStatusFilter(next);
  };

  const handleSortChange = (selected) => {
    if (selected?.value) setSortType(selected.value);
  };

  const handleShowMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchListings({ page: nextPage, statusFilter, sortType });
  };

  const renderBody = () => {
    if (loading && rows.length === 0) {
      return <div className="my-mls-row__skeleton" aria-hidden="true" />;
    }
    if (hasError && rows.length === 0) {
      return <ErrorState onRetry={() => fetchListings({ page: 1 })} />;
    }
    if (rows.length === 0) {
      if (statusFilter.key === "all") {
        return <PreSsoEmptyState />;
      }
      return <FilterEmptyState statusLabel={statusFilter.label} />;
    }

    return (
      <>
        {rows.map((row) => (
          <MyMlsListingsRow key={row.ListingKey} row={row} />
        ))}
        {hasNextPage && (
          <div className="text-center pt-3 pb-3 w-100">
            <button
              className="btn btn-primary"
              onClick={handleShowMore}
              disabled={loading}
            >
              {loading ? "Loading..." : "Show More"}
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="my-mls-tab">
      <div className="my-mls-tab__controls">
        <div className="tab-wrap">
          <Tabs
            activeKey={statusFilter.key}
            onSelect={handleStatusChange}
            className="my-mls-tab__status-tabs"
          >
            {STATUS_OPTIONS.map((opt) => (
              <Tab key={opt.key} eventKey={opt.key} title={opt.label} />
            ))}
          </Tabs>
        </div>
        <div className="my-mls-tab__sort">
          <Form.Label style={{ fontSize: 15 }}>Sort By:</Form.Label>
          <SelectDropDown
            id="my-mls-sort"
            name="my-mls-sort"
            options={SORT_OPTIONS}
            className="basic-single sort-select d-inline-block mb-0"
            classNamePrefix="select"
            placeholder="Select"
            value={SORT_OPTIONS.find((o) => o.value === sortType)}
            onChange={handleSortChange}
            errorClass="d-none"
          />
        </div>
      </div>

      {totalCount > 0 && (
        <div className="my-mls-tab__summary">
          Showing {rows.length} of {totalCount}{" "}
          {totalCount === 1 ? "listing" : "listings"}
        </div>
      )}

      {renderBody()}
    </div>
  );
};

export default MyMlsListingsTab;
